import { createFeatureSelector, createSelector } from '@ngrx/store';

import { OrbiMessageType } from '@patient-ui/shared/enums';
import { OrbiMessage } from '@patient-ui/shared/models';

import * as fromOrbi from './orbi.reducer';

export const orbiStateSelector = createFeatureSelector<fromOrbi.OrbiState>(
  fromOrbi.orbiFeatureKey
);

export const selectOrbiSessionId = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.sessionId
);

export const selectOrbiMessages = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.messages
);

export const selectOrbiSendStatus = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.sendMessageRequestStatus
);

/**
 * The messages to RENDER in the thread (PCSA-13160 / PCSA-13172).
 *
 * The turns themselves — including the "thinking" placeholder and the error
 * bubble — now come straight from the reducer, each in the slot its own send
 * reserved (PCSA-13312, see `orbiLoadingTurn`). They used to be SYNTHESIZED here
 * from the send state and appended at the foot of the thread, which is what the
 * 08/17 report was about: with two messages outstanding both questions stacked and
 * both answers landed underneath them, so no answer sat under the message it
 * answered. A single appended bubble also could not represent two outstanding
 * sends, or an error for one send alongside a thinking bubble for another.
 *
 * All this projection does now is decide whether the RETRY affordance is offered,
 * which is a property of the thread's shape rather than of any one turn: it is
 * offered only on a TRAILING error turn. `retryLastMessage` resends
 * `lastUserMessage` (the newest message sent) and removes the last two turns, so
 * on any earlier error it would re-ask the wrong question and delete somebody
 * else's turn. A trailing error means nothing was sent after the one that failed,
 * which also implies nothing is still outstanding — the same guard mobile applies
 * before retrying (`_messages.last.type != error` → return).
 *
 * The user-facing copy is mapped from the typed [OrbiError] in the reducer, so raw
 * codes/backend detail still never reach the thread (PCSA-13172 AC 1/2).
 */
export const selectOrbiThreadMessages = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState): OrbiMessage[] => {
    const lastIndex = state.messages.length - 1;

    return state.messages.map((message, index) =>
      message.type === OrbiMessageType.Error && index !== lastIndex
        ? { ...message, canRetry: false }
        : message
    );
  }
);

/**
 * Whether ANY send is still awaiting the agent (PCSA-13312) — read off the thread
 * itself, which is where a pending send is now represented: one reserved slot per
 * outstanding message.
 *
 * Distinct from `selectOrbiSendStatus === Pending`: the status describes the most
 * recent OUTCOME, so with a queue it flips to Success/Failure the moment the first
 * answer lands and would report a still-outstanding second send as finished. Same
 * definition mobile uses (a `loading` entry present in `messages`).
 */
export const selectOrbiSendPending = createSelector(
  selectOrbiMessages,
  (messages: OrbiMessage[]) =>
    messages.some((message) => message.type === OrbiMessageType.Loading)
);

export const selectOrbiError = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.error
);

export const selectOrbiLastUserMessage = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.lastUserMessage
);

export const selectOrbiLastRequestContext = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.lastRequestContext
);

/** Consumed by PCSA-13170's locked-conversation experience. */
export const selectChatLockedForSession = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.chatLockedForSession
);

/**
 * When the active conversation was created, or `null` for one started in this
 * browser session (design review 08/20 — the thread's date divider). Written only
 * by `resumeConversation`; `null` is the accurate answer rather than a missing
 * one, and the view formats it as today (`formatOrbiChatDate`).
 */
export const selectOrbiConversationStartedAt = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.startedAt
);

/** Submitted-feedback map keyed by messageId (PCSA-13171). */
export const selectOrbiFeedbackByMessageId = createSelector(
  orbiStateSelector,
  (state: fromOrbi.OrbiState) => state.feedbackByMessageId
);

/**
 * Whether a conversation is currently ACTIVE (PCSA-13441): the entry point opens
 * it instead of starting a fresh question.
 *
 * `messages.length > 0` covers both ways a conversation becomes active with one
 * condition, because both write the same slice:
 *  - the patient sent a first message this session (`sendMessage` appends the
 *    user turn optimistically), and
 *  - the patient resumed a conversation from history (PCSA-13169 —
 *    `resumeConversation` adopts that conversation's sessionId + messages).
 * It also resets itself: `startNewConversation` returns the slice to
 * `createOrbiInitialState()` (messages: []), and a logout / session timeout
 * tears the store down — so the entry point goes back to its initial input
 * state with no extra bookkeeping.
 */
export const selectOrbiConversationActive = createSelector(
  selectOrbiMessages,
  (messages) => messages.length > 0
);

export const orbiQuery = {
  orbiStateSelector,
  selectOrbiSessionId,
  selectOrbiMessages,
  selectOrbiThreadMessages,
  selectOrbiSendStatus,
  selectOrbiSendPending,
  selectOrbiError,
  selectOrbiLastUserMessage,
  selectOrbiLastRequestContext,
  selectChatLockedForSession,
  selectOrbiConversationStartedAt,
  selectOrbiFeedbackByMessageId,
  selectOrbiConversationActive,
};
